import utils from './utils';
import _ from 'lodash';
import tasks from './api_requests/tasks';

export const get_customer_form_suggestions = async (structure: any, set_suggestions_context: any) => {
	if (!structure || _.isEmpty(structure)) return;

	const uniqueValues: Record<string, Set<any>> = {};

	// Iterate through each object in the array
	_.forEach(structure, (obj) => {
		if (_.isObject(obj) && !_.isArray(obj)) {
			// Iterate through each key in the current object
			_.forEach(_.entries(obj), ([key, value]) => {
				// Special handling for phone numbers
				if (key === 'phone' && value && _.get(obj, 'country_code')) {
					// Initialize a Set for this key if it doesn't exist yet
					if (!uniqueValues[key]) {
						uniqueValues[key] = new Set();
					}
					// Combine country_code and phone with space in between
					const fullPhone = `${(obj as any).country_code} ${value}`;
					uniqueValues[key].add(fullPhone);
				}
				// Skip country_code as it's already processed with phone
				else if (_.includes(['country_code', 'id'], key)) {
					// Do nothing, we don't want country_code in the final result
				}
				// Normal processing for other keys
				else {
					// Initialize a Set for this key if it doesn't exist yet
					if (!uniqueValues[key]) {
						uniqueValues[key] = new Set();
					}
					if (value) {
						// Add the value to the Set (duplicates will be automatically filtered)
						uniqueValues[key].add(value);
					}
				}
			});
		}
	});

	// Convert Sets to Arrays for the final result
	const humanCalculatedObject = _.fromPairs(_.map(_.entries(uniqueValues), ([key, set]) => [key, Array.from(set)]));

	set_suggestions_context(humanCalculatedObject);

	const data = JSON.stringify(humanCalculatedObject, null, 2);

	const refactorPrompt = `
		Based on the provided information by the user, generate suggestions for each field by combining and splitting relevant fields to form logical, meaningful entries.
		I need you to generate predictions for various fields based on the given data, adhering strictly to the following rules:
		Rules:
		Field Suggestions: Only suggest values derived directly from the provided data.
		Empty Arrays: If no data is available for a specific field, return an empty array for that field.
		Name & Related Fields: For fields like "email," "phone," "name," "first_name," "last_name," "display_name," and "company_name," follow these rules:
			Rule 1: If "first_name" is "Tushar" and "last_name" is "Khatter," the "display_name" could be "Tushar Khatter."
			Rule 3: If "company_name" is "John Bakery," it should be split into "John" (first name) and "Bakery" (last name) for fields like "first_name" and "last_name."
			Rule 4: You can derive suggestions for "first_name" and "last_name" from "company_name" and "display_name" (split them logically). For example, if the company is "John Bakery," you should suggest "John" for first_name and "Bakery" for last_name. 
					Always break company_name and display_name to generate smart first_name and last_name.  and suggest those across all fields such as contacts, address and basic details
			Rule 5: If the "email" is "tushar.khatter@abc.com," you can split it into "tushar" (first name) and "khatter" (last name) for "first_name" and "last_name" respectively.
			Rule 6: For "first_name" or "last_name", try to infer it from the available data (e.g., using "company_name" or "display_name").
								
		Relevant Values Only: Do not generate random or irrelevant suggestions for fields like "designation," "phone," or "address" unless those values are explicitly provided in the actual data.
		Consistency Across Data: Ensure consistency across all keys (e.g., basic_details, contacts and addresses) for fields like first_name, last_name, email, phone, and any related attributes. 
		Avoid Repetition: Suggestions must be unique for each field and not repeat values across different fields unless contextually necessary.
		Sample Input data For your reference  : 

		{"first_name":["Ravish","Tushar","Deepam"],"last_name":["Agarwal","Khatter","Patel"],"phone":["+1684 8132424233","+1 8132424233","+91 9818532443"],"email":["ravish@wizcom.com","deepam@wiz.com"],"country":["usa"],"street_address":["123 William St","New Smyrna Beach, North Atlantic Avenue","White House Visitor Center, 1450, Pennsylvania Avenue Northwest"],"address_line_2":["Ab ded","Street 1244","1800"],"city":["New York","New Smyrna Beach","Washington"],"state":["New York","Florida","District of Columbia"],"pincode":["10038","32169","20004"],"type":["billing","shipping"],"designation":["Manager"],"company_name":["123 FURNISHING BARON",""],"display_name":["123 FURNISHING BARON"],"sales_reps":["a4c2e3e7-b087-4116-b45d-3312164c8f5a"],"catalog_group":["8d397d90-5ee8-440a-9cb6-4ad3726bb12c"],"type_of_customer":[],"system_id":["C_00028"]}

		Sample Response expected

		{"first_name":["Ravish","Tushar","Deepam","Lokesh","123"],"last_name":["Agarwal","Khatter","Patel","Automobiles","FURNISHING BARON"],"phone":["+1684 8132424233","+1 8132424233","+91 9818532443"],"email":["ravish@wizcom.com","deepam@wiz.com"],"country":["usa"],"street_address":["123 William St","New Smyrna Beach, North Atlantic Avenue","White House Visitor Center, 1450, Pennsylvania Avenue Northwest"],"address_line_2":["Ab ded","Street 1244","1800"],"city":["New York","New Smyrna Beach","Washington"],"state":["New York","Florida","District of Columbia"],"pincode":["10038","32169","20004"],"type":["billing","shipping"],"designation":["Manager"],"company_name":["123 FURNISHING BARON","Ravish Agarwal","Ravish Khatter","Ravish Patel","Tushar Agarwal","Tushar Khatter","Tushar Patel","Deepam Agarwal","Deepam Khatter","Deepam Patel"],"display_name":["Lokesh Automobiles","Ravish Agarwal","Ravish Khatter","Ravish Patel","Tushar Agarwal","Tushar Khatter","Tushar Patel","Deepam Agarwal","Deepam Khatter","Deepam Patel"],"sales_reps":["a4c2e3e7-b087-4116-b45d-3312164c8f5a"],"catalog_group":["8d397d90-5ee8-440a-9cb6-4ad3726bb12c"],"type_of_customer":[],"system_id":["C_00028"]}

		Note 3: Make sure not to remove any data from the provided data like email or name. Only generate suggestions based on the available data.
		Make sure to focus on logical combinations and splits from the provided data while avoiding random or irrelevant suggestions. Only use values that are available in the provided data to ensure accurate and relevant suggestions.
		Only return the JSON without any markdown, extra text, or explanation.
		The expected output should be a JSON object with each field containing suggestions based on the provided data, ensuring that the suggestions are relevant, unique, and based solely on the data available. 
		Make sure to follow the rules strictly and provide a well-structured JSON response and no markdown.`;

	try {
		const payload = {
			model: 'gpt-4o-mini',
			messages: [
				{
					role: 'user',
					content: refactorPrompt,
				},
				{
					role: 'user',
					content: data,
				},
			],
			store: true,
			temperature: 0.2,
			prediction: {
				type: 'content',
				content: data,
			},
		};

		const responseData: any = await tasks.ai_auto_complete(payload);

		const result = utils.parse_and_check_json(responseData.choices[0].message.content) || {};
		set_suggestions_context(result);
	} catch (error) {
		console.error(error);
	}
};

export const analyze_sync_log = async (syncMessage: string, syncStatus: string, syncAction: string, integrationType: string) => {
	if (!syncMessage) {
		return null;
	}

	const analysisPrompt = `
		You are a helpful support assistant. Analyze this ${integrationType || 'system'} sync error and provide clear, actionable solutions.
		
		Context:
		- Sync Status: ${syncStatus}
		- Sync Action: ${syncAction}
		- Error Message: ${syncMessage}
		- Integration: ${integrationType || 'Unknown'}
		
		Instructions:
		1. Use simple, non-technical language that any user can understand
		2. Focus on practical steps the user can take right now
		3. For failed syncs: Identify the problem and provide 2-3 clear steps to fix it
		4. For skipped syncs: Explain why it was skipped and what action (if any) is needed
		5. Prioritize the quickest/easiest solutions first
		6. If it's a data issue, specify exactly what needs to be checked or corrected
		7. Keep the total response under 100 words
		8. Be empathetic and reassuring - this is fixable!
		
		QuickBooks-specific guidance:
		- If error mentions "QBD Connection" or "timed out": Ask client to restart QBD, then update web services (File > App Management > Update Web Services). Remind them to click Hide, not Exit.
		- If error 3100: Customer name already exists → Rename or map the customer before pushing.
		- If error 3120: Customer deleted in QBD → Confirm and push as new customer.
		- If error 3000: The given object ID "" in the field "list id" is invalid. - Customer of the order is not pushed, please push the customer first.
		- If error 3140: Item missing → Ask client to add SKU in QBD, then retry.
		
		Format your response as:
		{
			"summary": "What happened in simple terms",
			"root_cause": "Why this happened",
			"next_steps": [
				"First thing to try",
				"If that doesn't work, try this",
				"Contact support if needed"
			]
		}
		
		Return only valid JSON without any markdown or extra text.
	`;

	try {
		const payload = {
			model: 'gpt-4o-mini',
			messages: [
				{
					role: 'system',
					content:
						'You are a technical support assistant specializing in system integration and sync operations. Provide clear, actionable guidance.',
				},
				{
					role: 'user',
					content: analysisPrompt,
				},
			],
			temperature: 0.3,
			max_tokens: 500,
		};
		const response: any = await tasks.ai_chat_completions(payload);
		const content = response?.choices?.[0]?.message?.content;
		if (!content) return null;
		return utils.parse_and_check_json(content);
	} catch (error) {
		console.error('Error analyzing sync log:', error);
		return null;
	}
};
